import { Button, Dialog, ButtonType } from "@constellation-academy/epic-ui";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useTranslation } from "@i18n/hooks";
import { error100, shade0 } from "@themes/colors";
import { ButtonsWrapper } from "./confirm-with-text-dialog.styles";
import {
	type ConfirmWithTextDialogProps,
	type ConfirmWithTextFormState,
} from "./confirm-with-text-dialog.types";
import { ButtonVariant } from "../button";
import { DefaultTextFieldComponent } from "../DefaultTextInput";
import { ValidatedField } from "../ValidatedField";

export const ConfirmWithTextDialog = ({
	showDialog = true,
	setShowDialog,
	title,
	content,
	textToConfirm,
	onCancel,
	onConfirm,
}: ConfirmWithTextDialogProps) => {
	const { t } = useTranslation(["common", "validation"]);

	const form = useFormik<ConfirmWithTextFormState>({
		initialValues: {
			textToConfirm: "",
		},
		validationSchema: Yup.object().shape({
			textToConfirm: Yup.string()
				.equals([textToConfirm], t("validation:textMismatch"))
				.required(t("validation:confirmDeletion")),
		}),
		onSubmit: () => {
			handleConfirm();
		},
	});

	const handleClose = () => {
		onCancel?.();
		setShowDialog?.(false);
	};

	const handleConfirm = () => {
		onConfirm?.();
		setShowDialog?.(false);
	};

	return (
		<Dialog visible={showDialog} onHide={handleClose} title={title}>
			{content}
			<form onSubmit={form.handleSubmit} className="mt-4">
				<ValidatedField<ConfirmWithTextFormState, string>
					formikConfig={form}
					name="textToConfirm"
					placeholder={textToConfirm}
					component={DefaultTextFieldComponent}
					helpText={t("common:confirmDialog.helpText", { text: textToConfirm })}
				/>
				<ButtonsWrapper>
					<Button
						onClick={handleClose}
						type={ButtonType.Button}
						label={t("common:actions.cancel")}
					/>
					<Button
						variant={ButtonVariant.Error}
						backgroundColor={error100}
						contentColor={shade0}
						type={ButtonType.Submit}
						label={t("common:actions.yes")}
					/>
				</ButtonsWrapper>
			</form>
		</Dialog>
	);
};
